import { Checkbox, FormControl, ListItemText, MenuItem, Select, TextField, Dialog, DialogTitle, DialogContent, IconButton, Box } from '@mui/material';
import { useEffect, useState } from 'react';
import ClearFilterButton from './ClearFilterButton';
import { LOCATION_DATA } from 'util/locationData';
import CloseIcon from '@mui/icons-material/Close';

interface LocationFilterProps {
    placeholder: string;
    value: {
        region?: string;
        locations?: Array<{
            city: string;
            neighborhoods: string[];
        }>;
    };
    onChanged: (value: {
        region?: string;
        locations?: Array<{
            city: string;
            neighborhoods: string[];
        }>;
    }) => void;
    showClearButton?: boolean;
    displayClassName?: string;
    'data-cy'?: string;
}

interface SelectedLocations {
    regions: Set<string>;
    areas: Set<string>;
    neighborhoods: Set<string>;
}

const LocationFilter = ({
    value,
    onChanged,
    placeholder = 'Select Location',
    showClearButton = true,
    displayClassName = '',
    'data-cy': dataCy,
}: LocationFilterProps) => {
    const [selectedLocations, setSelectedLocations] = useState<SelectedLocations>({
        regions: new Set(),
        areas: new Set(),
        neighborhoods: new Set(),
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [open, setOpen] = useState(false);

    // Initialize expanded states with all regions and areas
    const [expandedRegions] = useState<Set<string>>(() => new Set(LOCATION_DATA.map((region) => region.region)));
    const [expandedAreas] = useState<Set<string>>(() => new Set(LOCATION_DATA.flatMap((region) => region.areas.map((area) => area.area))));

    // Normalize text by removing accents and special characters
    const normalizeText = (text: string): string => {
        return text
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
            .replace(/[^a-z0-9\s]/g, ''); // Remove special characters except spaces
    };

    useEffect(() => {
        // Update selected locations when value changes
        if (value?.locations) {
            const newSelectedLocations: SelectedLocations = {
                regions: new Set(),
                areas: new Set(),
                neighborhoods: new Set(),
            };

            // For each location in the store
            value.locations.forEach((location) => {
                // Find the region that contains this city
                const regionData = LOCATION_DATA.find((r) => r.areas.some((a) => a.area === location.city));

                if (regionData) {
                    // Add the region
                    newSelectedLocations.regions.add(regionData.region);
                    // Add the city
                    newSelectedLocations.areas.add(location.city);
                    // Add all neighborhoods for this city
                    location.neighborhoods.forEach((neighborhood) => {
                        newSelectedLocations.neighborhoods.add(neighborhood);
                    });
                }
            });

            setSelectedLocations(newSelectedLocations);
        } else {
            setSelectedLocations({
                regions: new Set(),
                areas: new Set(),
                neighborhoods: new Set(),
            });
        }
    }, [value]);

    const handleRegionClick = (event: React.MouseEvent, region: string) => {
        event.preventDefault();
        event.stopPropagation();

        const newSelectedLocations = { ...selectedLocations };
        const regionData = LOCATION_DATA.find((r) => r.region === region);

        if (!regionData) return;

        // Get all areas in this region
        const allAreasInRegion = regionData.areas;

        // Check if all areas in this region are currently selected
        const allAreasSelected = allAreasInRegion.every((area) => newSelectedLocations.areas.has(area.area));

        if (allAreasSelected) {
            // If all areas are selected, deselect them all
            allAreasInRegion.forEach((area) => {
                newSelectedLocations.areas.delete(area.area);
                // Also remove any selected neighborhoods from these areas
                area.neighborhoods.forEach((neighborhood) => {
                    newSelectedLocations.neighborhoods.delete(neighborhood);
                });
            });
        } else {
            // If not all areas are selected, select all areas in this region
            allAreasInRegion.forEach((area) => {
                newSelectedLocations.areas.add(area.area);
            });
        }

        setSelectedLocations(newSelectedLocations);

        // Create location objects array with proper neighborhood associations
        const locationObjects = Array.from(newSelectedLocations.areas).map((area) => {
            const areaData = LOCATION_DATA.flatMap((r) => r.areas).find((a) => a.area === area);
            if (!areaData) return { city: area, neighborhoods: [] };

            // Only include neighborhoods that belong to this specific area
            const neighborhoods = Array.from(newSelectedLocations.neighborhoods).filter((n) => areaData.neighborhoods.includes(n));

            return {
                city: area,
                neighborhoods: neighborhoods,
            };
        });

        onChanged({
            region: region,
            locations: locationObjects,
        });
    };

    const handleAreaClick = (event: React.MouseEvent, region: string, area: string) => {
        event.preventDefault();
        event.stopPropagation();

        const newSelectedLocations = { ...selectedLocations };
        const regionData = LOCATION_DATA.find((r) => r.region === region);

        if (!regionData) return;

        // Toggle the area selection
        if (newSelectedLocations.areas.has(area)) {
            newSelectedLocations.areas.delete(area);
            // Remove any selected neighborhoods from this area
            regionData.areas
                .find((a) => a.area === area)
                ?.neighborhoods.forEach((neighborhood) => {
                    newSelectedLocations.neighborhoods.delete(neighborhood);
                });
        } else {
            newSelectedLocations.areas.add(area);
        }

        setSelectedLocations(newSelectedLocations);

        // Create location objects array with proper neighborhood associations
        const locationObjects = Array.from(newSelectedLocations.areas).map((area) => {
            const areaData = LOCATION_DATA.flatMap((r) => r.areas).find((a) => a.area === area);
            if (!areaData) return { city: area, neighborhoods: [] };

            // Only include neighborhoods that belong to this specific area
            const neighborhoods = Array.from(newSelectedLocations.neighborhoods).filter((n) => areaData.neighborhoods.includes(n));

            return {
                city: area,
                neighborhoods: neighborhoods,
            };
        });

        onChanged({
            region: region,
            locations: locationObjects,
        });
    };

    const handleNeighborhoodClick = (event: React.MouseEvent, region: string, area: string, neighborhood: string) => {
        event.preventDefault();
        event.stopPropagation();

        const newSelectedLocations = { ...selectedLocations };
        if (newSelectedLocations.neighborhoods.has(neighborhood)) {
            newSelectedLocations.neighborhoods.delete(neighborhood);
        } else {
            newSelectedLocations.neighborhoods.add(neighborhood);
            // Ensure parent area is selected
            newSelectedLocations.areas.add(area);
        }

        setSelectedLocations(newSelectedLocations);

        // Create location objects array with proper neighborhood associations
        const locationObjects = Array.from(newSelectedLocations.areas).map((area) => {
            const areaData = LOCATION_DATA.flatMap((r) => r.areas).find((a) => a.area === area);
            if (!areaData) return { city: area, neighborhoods: [] };

            // Only include neighborhoods that belong to this specific area
            const neighborhoods = Array.from(newSelectedLocations.neighborhoods).filter((n) => areaData.neighborhoods.includes(n));

            return {
                city: area,
                neighborhoods: neighborhoods,
            };
        });

        onChanged({
            region: region,
            locations: locationObjects,
        });
    };

    const clearFilterClicked = () => {
        setSelectedLocations({
            regions: new Set(),
            areas: new Set(),
            neighborhoods: new Set(),
        });
        onChanged({});
    };

    const renderValue = () => {
        if (!value?.locations || value.locations.length === 0) {
            return placeholder;
        }

        const selectedItems = value.locations.map((loc) => `${loc.city}${loc.neighborhoods.length > 0 ? ` (${loc.neighborhoods.join(', ')})` : ''}`);

        return selectedItems.join(', ');
    };

    // Sort the data alphabetically
    const sortedLocationData = [...LOCATION_DATA].sort((a, b) => {
        // Define regions that should be at the top
        const topRegions = ['Mallorca: Southwest', 'Palma / Son Vida'];
        // Define regions that should be at the bottom
        const bottomRegions = ['Formentera', 'Ibiza', 'Menorca'];

        // Check if either region is in the top or bottom regions list
        const aIsTop = topRegions.some((region) => a.region === region);
        const bIsTop = topRegions.some((region) => b.region === region);
        const aIsBottom = bottomRegions.some((region) => a.region.includes(region));
        const bIsBottom = bottomRegions.some((region) => b.region.includes(region));

        // If one is top and other isn't, sort accordingly
        if (aIsTop && !bIsTop) return -1;
        if (!aIsTop && bIsTop) return 1;

        // If one is bottom and other isn't, sort accordingly
        if (aIsBottom && !bIsBottom) return 1;
        if (!aIsBottom && bIsBottom) return -1;

        // If both are top regions, sort them in the order defined in topRegions
        if (aIsTop && bIsTop) {
            return topRegions.indexOf(a.region) - topRegions.indexOf(b.region);
        }

        // If both are bottom regions or both are regular regions, sort alphabetically
        return a.region.localeCompare(b.region);
    });

    // Filter function to check if a location matches the search term
    const matchesSearch = (text: string) => {
        const normalizedText = normalizeText(text);
        const normalizedSearch = normalizeText(searchTerm);
        return normalizedText.includes(normalizedSearch);
    };

    // Filter the location data based on search term
    const filteredLocationData = (
        searchTerm
            ? LOCATION_DATA.map((region) => {
                  // First filter neighborhoods within areas
                  const filteredAreas = region.areas.map((area) => ({
                      ...area,
                      neighborhoods: area.neighborhoods.filter((neighborhood) => matchesSearch(neighborhood)),
                  }));

                  // Then filter areas that either match the search or have matching neighborhoods
                  const matchingAreas = filteredAreas.filter((area) => matchesSearch(area.area) || area.neighborhoods.length > 0);

                  // Only include the region if it matches the search or has matching areas
                  if (matchesSearch(region.region) || matchingAreas.length > 0) {
                      return {
                          ...region,
                          areas: matchingAreas,
                      };
                  }
                  return null;
              }).filter((region): region is (typeof LOCATION_DATA)[0] => region !== null)
            : sortedLocationData
    ) as typeof LOCATION_DATA;

    // Get filtered areas for a region based on search term
    const getFilteredAreas = (region: (typeof LOCATION_DATA)[0]) => {
        return region.areas.filter((area) => matchesSearch(area.area) || area.neighborhoods.some((neighborhood) => matchesSearch(neighborhood)));
    };

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    return (
        <div className="relative w-full h-full">
            <FormControl fullWidth className="!h-full">
                <Select
                    multiple
                    value={Array.from(selectedLocations.areas)}
                    displayEmpty
                    renderValue={renderValue}
                    className={`!w-full !h-full rounded-[8px] border-[#DFE1E1] leading-6 px-1 ${displayClassName}`}
                    onClick={handleOpen}
                    open={false}
                />
            </FormControl>

            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        maxHeight: '80vh',
                        minHeight: '60vh',
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        m: 0,
                        p: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        borderBottom: '1px solid #DFE1E1',
                    }}
                >
                    <span className="text-lg font-semibold">Select Locations</span>
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        sx={{
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                    <Box sx={{ p: 2, position: 'sticky', top: 0, bgcolor: 'white', zIndex: 1, borderBottom: '1px solid #DFE1E1' }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search locations..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: 'white',
                                    '& fieldset': {
                                        borderColor: '#DFE1E1',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#DFE1E1',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#DFE1E1',
                                    },
                                },
                            }}
                        />
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            maxHeight: 'calc(80vh - 140px)',
                            overflow: 'auto',
                        }}
                    >
                        {filteredLocationData.map((regionData) => {
                            const filteredAreas = getFilteredAreas(regionData);
                            const originalRegionData = LOCATION_DATA.find((r) => r.region === regionData.region);
                            if (!originalRegionData) return null;

                            const selectedAreasInRegion = originalRegionData.areas.filter((area) => selectedLocations.areas.has(area.area));
                            const totalAreasInRegion = originalRegionData.areas.length;
                            const hasSelectedAreas = selectedAreasInRegion.length > 0;
                            const allAreasSelected = selectedAreasInRegion.length === totalAreasInRegion;

                            return (
                                <div key={regionData.region}>
                                    <MenuItem
                                        onClick={(e) => handleRegionClick(e, regionData.region)}
                                        sx={{
                                            cursor: 'pointer',
                                            py: 1.5,
                                            '&:hover': {
                                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                            },
                                        }}
                                    >
                                        <Checkbox
                                            checked={allAreasSelected}
                                            indeterminate={hasSelectedAreas && !allAreasSelected}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRegionClick(e, regionData.region);
                                            }}
                                            sx={{
                                                padding: '0 8px 0 0',
                                                '&:hover': {
                                                    backgroundColor: 'transparent',
                                                },
                                                '& .MuiSvgIcon-root': {
                                                    fontSize: '20px',
                                                },
                                            }}
                                        />
                                        <ListItemText
                                            primary={regionData.region}
                                            sx={{
                                                m: 0,
                                                '& .MuiTypography-root': {
                                                    fontWeight: 600,
                                                },
                                            }}
                                        />
                                    </MenuItem>
                                    {filteredAreas.map((area) => (
                                        <div key={area.area}>
                                            <MenuItem
                                                onClick={(e) => handleAreaClick(e, regionData.region, area.area)}
                                                sx={{
                                                    pl: 4,
                                                    py: 1.5,
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                    },
                                                }}
                                            >
                                                <Checkbox
                                                    checked={selectedLocations.areas.has(area.area)}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleAreaClick(e, regionData.region, area.area);
                                                    }}
                                                    sx={{
                                                        padding: '0 8px 0 0',
                                                        '&:hover': {
                                                            backgroundColor: 'transparent',
                                                        },
                                                        '& .MuiSvgIcon-root': {
                                                            fontSize: '20px',
                                                        },
                                                    }}
                                                />
                                                <ListItemText
                                                    primary={area.area}
                                                    sx={{
                                                        m: 0,
                                                    }}
                                                />
                                            </MenuItem>
                                            {area.neighborhoods.map((neighborhood) => (
                                                <MenuItem
                                                    key={neighborhood}
                                                    onClick={(e) => handleNeighborhoodClick(e, regionData.region, area.area, neighborhood)}
                                                    sx={{
                                                        pl: 8,
                                                        py: 1.5,
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                        },
                                                    }}
                                                >
                                                    <Checkbox
                                                        checked={selectedLocations.neighborhoods.has(neighborhood)}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleNeighborhoodClick(e, regionData.region, area.area, neighborhood);
                                                        }}
                                                        sx={{
                                                            padding: '0 8px 0 0',
                                                            '&:hover': {
                                                                backgroundColor: 'transparent',
                                                            },
                                                            '& .MuiSvgIcon-root': {
                                                                fontSize: '20px',
                                                            },
                                                        }}
                                                    />
                                                    <ListItemText
                                                        primary={neighborhood}
                                                        sx={{
                                                            m: 0,
                                                        }}
                                                    />
                                                </MenuItem>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            );
                        })}
                    </Box>
                </DialogContent>
            </Dialog>

            {/* clear button */}
            {value?.locations && value.locations.length > 0 && showClearButton && <ClearFilterButton onClicked={clearFilterClicked} />}
        </div>
    );
};

export default LocationFilter;
